import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { rankingsApi } from "../api";
import type { Mypage } from "../api/rankings";

interface MypageContextValue {
  mypage: Mypage | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const MypageContext = createContext<MypageContextValue | null>(null);

export const MypageProvider: React.FC<{
  isAuthenticated: boolean;
  children: React.ReactNode;
}> = ({ isAuthenticated, children }) => {
  const [mypage, setMypage] = useState<Mypage | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMypage = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await rankingsApi.getMyPage();
      setMypage(data);
    } catch {
      setError("マイページの取得に失敗しました");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchMypage();
    } else {
      setMypage(null);
    }
  }, [isAuthenticated, fetchMypage]);

  return (
    <MypageContext.Provider
      value={{ mypage, isLoading, error, refresh: fetchMypage }}
    >
      {children}
    </MypageContext.Provider>
  );
};

export const useMypageContext = (): MypageContextValue => {
  const ctx = useContext(MypageContext);
  if (!ctx)
    throw new Error("useMypageContext must be used within MypageProvider");
  return ctx;
};
